import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ConfigurationList.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function ConfigurationList({ configurations, onEdit, onDelete, onDestroyResources, onDeploy, onUpdate, onBuild }) {
  const [statuses, setStatuses] = useState({});
  const [deployingIds, setDeployingIds] = useState(new Set());
  const [destroyingIds, setDestroyingIds] = useState(new Set());
  const [buildingIds, setBuildingIds] = useState(new Set());
  const [showLogs, setShowLogs] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showBuilderLogs, setShowBuilderLogs] = useState(null);
  const [builderLogs, setBuilderLogs] = useState([]);
  const [builderStatus, setBuilderStatus] = useState(null);
  const [showBackups, setShowBackups] = useState(null);
  const [backups, setBackups] = useState([]);
  const [restoring, setRestoring] = useState(false);
  const [showRestoreTerraform, setShowRestoreTerraform] = useState(false);
  const [terraformId, setTerraformId] = useState('');
  const [restoringTerraform, setRestoringTerraform] = useState(false);
  const [showSyncAppEnv, setShowSyncAppEnv] = useState(null);
  const [syncAppEnvContent, setSyncAppEnvContent] = useState('');
  const [syncAppEnvServerIp, setSyncAppEnvServerIp] = useState(null);
  const [syncingAppEnv, setSyncingAppEnv] = useState(false);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [showUpdateLogs, setShowUpdateLogs] = useState(null);
  const [updateLogs, setUpdateLogs] = useState([]);
  const [updateStatus, setUpdateStatus] = useState(null);

  useEffect(() => {
    const loadStatuses = async () => {
      const statusPromises = configurations.map(async (config) => {
        try {
          const res = await axios.get(`${API_BASE}/configurations/${config.id}/status`);
          return { id: config.id, status: res.data };
        } catch (error) {
          return { id: config.id, status: { status: 'unknown' } };
        }
      });
      const results = await Promise.all(statusPromises);
      const statusMap = {};
      results.forEach(r => {
        statusMap[r.id] = r.status;
      });
      setStatuses(statusMap);
      const destroying = results.filter(r => r.status.status === 'destroying').map(r => r.id);
      if (destroying.length > 0) {
        setDestroyingIds(prev => new Set([...prev, ...destroying]));
      }
    };

    if (configurations.length > 0) {
      loadStatuses();
    }
  }, [configurations]);

  useEffect(() => {
    if (buildingIds.size === 0) return;
    const interval = setInterval(async () => {
      const ids = Array.from(buildingIds);
      for (const id of ids) {
        try {
          const res = await axios.get(`${API_BASE}/configurations/${id}/build/status`);
          if (res.data && (res.data.status === 'completed' || res.data.status === 'failed')) {
            setBuildingIds(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }
        } catch (_) {
          // Builder status not found or error - leave in set, will retry
        }
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [buildingIds]);

  useEffect(() => {
    if (updatingIds.size === 0) return;
    const interval = setInterval(async () => {
      const ids = Array.from(updatingIds);
      for (const id of ids) {
        try {
          const res = await axios.get(`${API_BASE}/configurations/${id}/update/status`);
          if (res.data && (res.data.status === 'completed' || res.data.status === 'failed')) {
            setUpdatingIds(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
            if (showUpdateLogs === id) {
              setUpdateLogs(res.data.logs || []);
              setUpdateStatus(res.data);
            }
            if (res.data.status === 'failed') {
              alert('Update failed: ' + (res.data.error || 'Unknown error'));
            }
          }
        } catch (_) {
          // Update status not found - leave in set, will retry
        }
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [updatingIds, showUpdateLogs]);

  useEffect(() => {
    // Poll for status updates for deploying or destroying configurations
    const idsToPoll = new Set([...deployingIds, ...destroyingIds]);
    if (idsToPoll.size > 0) {
      const interval = setInterval(async () => {
        const statusPromises = Array.from(idsToPoll).map(async (id) => {
          try {
            const res = await axios.get(`${API_BASE}/configurations/${id}/status`);
            return { id, status: res.data };
          } catch (error) {
            return { id, status: { status: 'unknown' } };
          }
        });
        const results = await Promise.all(statusPromises);
        setStatuses(prevStatuses => {
          const statusMap = { ...prevStatuses };
          results.forEach(r => {
            statusMap[r.id] = r.status;
            if (r.status.status !== 'in_progress') {
              setDeployingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(r.id);
                return newSet;
              });
            }
            if (r.status.status !== 'destroying') {
              setDestroyingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(r.id);
                return newSet;
              });
            }
          });
          return statusMap;
        });
      }, 3003);
      return () => clearInterval(interval);
    }
  }, [deployingIds, destroyingIds]);

  useEffect(() => {
    // Load logs if modal is open
    if (showLogs) {
      const loadLogs = async () => {
        try {
          const res = await axios.get(`${API_BASE}/configurations/${showLogs}/logs`);
          setLogs(res.data.logs || []);
        } catch (error) {
          console.error('Error loading logs:', error);
        }
      };
      loadLogs();
      const interval = setInterval(loadLogs, 2000);
      return () => clearInterval(interval);
    }
  }, [showLogs]);

  useEffect(() => {
    // Load builder logs if modal is open
    if (showBuilderLogs) {
      const loadBuilderLogs = async () => {
        try {
          const res = await axios.get(`${API_BASE}/configurations/${showBuilderLogs}/build/status`);
          const status = res.data;
          setBuilderStatus(status);
          setBuilderLogs(status.logs || []);
          if (status?.status === 'in_progress') {
            setBuildingIds(prev => new Set(prev).add(showBuilderLogs));
          }
          return status;
        } catch (error) {
          console.error('Error loading builder logs:', error);
          return null;
        }
      };
      loadBuilderLogs();
      // Poll every 2 seconds if builder is in progress
      const interval = setInterval(async () => {
        const status = await loadBuilderLogs();
        // Stop polling if builder is completed or failed
        if (status && (status.status === 'completed' || status.status === 'failed')) {
          clearInterval(interval);
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [showBuilderLogs]);

  useEffect(() => {
    if (showUpdateLogs) {
      const loadUpdateLogs = async () => {
        try {
          const res = await axios.get(`${API_BASE}/configurations/${showUpdateLogs}/update/status`);
          setUpdateStatus(res.data);
          setUpdateLogs(res.data.logs || []);
          if (res.data?.status === 'in_progress') {
            setUpdatingIds(prev => new Set(prev).add(showUpdateLogs));
          }
          return res.data;
        } catch (error) {
          console.error('Error loading update logs:', error);
          return null;
        }
      };
      loadUpdateLogs();
      const interval = setInterval(async () => {
        const status = await loadUpdateLogs();
        if (status && (status.status === 'completed' || status.status === 'failed')) {
          clearInterval(interval);
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [showUpdateLogs]);

  const getStatusBadge = (status) => {
    if (!status || status.status === 'not_deployed') {
      return <span className="badge badge-gray">Not Deployed</span>;
    }
    if (status.status === 'in_progress') {
      return <span className="badge badge-info">Deploying...</span>;
    }
    if (status.status === 'destroying') {
      return <span className="badge badge-info">Destroying...</span>;
    }
    if (status.status === 'destroyed') {
      return <span className="badge badge-gray">Destroyed</span>;
    }
    if (status.status === 'failed') {
      return <span className="badge badge-danger">Failed</span>;
    }
    if (status.status === 'deployed') {
      return <span className="badge badge-success">Deployed</span>;
    }
    return <span className="badge badge-warning">Unknown</span>;
  };

  const handleDeployClick = async (id) => {
    if (window.confirm('Deploy this configuration?')) {
      setDeployingIds(prev => new Set(prev).add(id));
      onDeploy(id);
    }
  };

  const loadBackups = async () => {
    try {
      const res = await axios.get(`${API_BASE}/backups`);
      setBackups(res.data);
    } catch (error) {
      console.error('Error loading backups:', error);
      alert('Failed to load backups');
    }
  };

  const handleShowBackups = async (configId) => {
    setShowBackups(configId);
    await loadBackups();
  };

  const handleRestore = async (timestamp, configId = null) => {
    if (!window.confirm('Restore from this backup? This will overwrite current configuration.')) {
      return;
    }

    setRestoring(true);
    try {
      await axios.post(`${API_BASE}/backups/${timestamp}/restore`, { configId });
      alert('Configuration restored successfully! Please refresh the page.');
      window.location.reload();
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert('Failed to restore backup: ' + (error.response?.data?.error || error.message));
    } finally {
      setRestoring(false);
    }
  };

  const handleRestoreFromTerraform = () => {
    setShowRestoreTerraform(true);
    setTerraformId('');
  };

  const handleRestoreTerraformSubmit = async () => {
    if (!terraformId || !terraformId.trim()) {
      alert('Please enter terraform directory ID');
      return;
    }

    if (!window.confirm(`Restore configuration from terraform directory "${terraformId}"?`)) {
      return;
    }

    setRestoringTerraform(true);
    try {
      const response = await axios.post(`${API_BASE}/configurations/restore-from-terraform/${terraformId.trim()}`);
      alert('Configuration restored successfully! Please refresh the page.');
      window.location.reload();
    } catch (error) {
      console.error('Error restoring from terraform:', error);
      alert('Failed to restore from terraform: ' + (error.response?.data?.error || error.message));
    } finally {
      setRestoringTerraform(false);
      setShowRestoreTerraform(false);
    }
  };

  return (
    <>
      <div className="configuration-list">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Saved Configurations</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              className="btn btn-sm btn-info" 
              onClick={handleRestoreFromTerraform}
              title="Restore configuration from terraform directory"
            >
              🔄 Restore from Terraform
            </button>
            <button 
              className="btn btn-sm btn-secondary" 
              onClick={handleShowBackups}
              title="View all backups and restore configurations"
            >
              📦 View Backups
            </button>
          </div>
        </div>
      {configurations.length === 0 ? (
        <div className="empty-state">
          <p>No saved configurations. Create a new one to get started.</p>
        </div>
      ) : (
        <div className="config-grid">
          {configurations.map((config) => (
            <div key={config.id} className="config-card">
              <div className="config-header">
                <h3>{config.domainPrefix || 'dc'}.{config.cloudflareZone || 'exolog.tech'}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {getStatusBadge(statuses[config.id])}
                  {config.services?.includes('exolog') && buildingIds.has(config.id) && (
                    <span className="badge badge-info">Building...</span>
                  )}
                </div>
              </div>
              
              <div className="config-info">
                <div className="info-row">
                  <span className="label">Server Type:</span>
                  <span className="value">{config.serverType}</span>
                </div>
                <div className="info-row">
                  <span className="label">Services:</span>
                  <span className="value">{config.services?.length || 0} selected</span>
                </div>
                {statuses[config.id]?.ip && (
                  <div className="info-row">
                    <span className="label">IP:</span>
                    <span className="value">{statuses[config.id].ip}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="label">Created:</span>
                  <span className="value">
                    {new Date(config.createdAt).toLocaleDateString('en-US')}
                  </span>
                </div>
              </div>

              <div className="config-actions">
                <button 
                  className="btn btn-sm btn-secondary" 
                  onClick={() => onEdit(config)}
                  disabled={statuses[config.id]?.status === 'in_progress'}
                >
                  Edit
                </button>
                <button 
                  className="btn btn-sm btn-success" 
                  onClick={() => handleDeployClick(config.id)}
                  disabled={statuses[config.id]?.status === 'in_progress'}
                >
                  {statuses[config.id]?.status === 'in_progress' ? 'Deploying...' : 'Deploy'}
                </button>
                {(statuses[config.id]?.status === 'in_progress' || statuses[config.id]?.status === 'failed') && (
                  <button 
                    className="btn btn-sm btn-info" 
                    onClick={() => setShowLogs(config.id)}
                  >
                    View Logs
                  </button>
                )}
                {(statuses[config.id]?.status === 'deployed' || statuses[config.id]?.status === 'completed') ? (
                  <>
                    <button 
                      className="btn btn-sm btn-primary" 
                      onClick={async () => {
                        if (!onUpdate) return;
                        if (!window.confirm('Update code on the server?\n\nThis will:\n- Pull latest code from git repository\n- Rebuild dependencies\n- Restart services')) return;
                        setUpdatingIds(prev => new Set(prev).add(config.id));
                        setShowUpdateLogs(config.id);
                        try {
                          await onUpdate(config.id);
                        } catch (err) {
                          setUpdatingIds(prev => { const n = new Set(prev); n.delete(config.id); return n; });
                          setShowUpdateLogs(null);
                          alert('Update failed: ' + (err.response?.data?.error || err.response?.data?.details || err.message));
                        }
                      }}
                      disabled={statuses[config.id]?.status === 'in_progress' || updatingIds.has(config.id)}
                      title="Update code on server (git pull + rebuild + restart)"
                    >
                      {updatingIds.has(config.id) ? 'Updating...' : 'Update Code'}
                    </button>
                    <button 
                      className="btn btn-sm btn-info" 
                      onClick={() => setShowUpdateLogs(config.id)}
                      title="View update logs"
                    >
                      Update Logs
                    </button>
                    {config.services?.includes('exolog') && (
                      <>
                        <button 
                          className="btn btn-sm btn-warning" 
                          onClick={() => {
                            if (onBuild) {
                              onBuild(config.id);
                              setBuildingIds(prev => new Set(prev).add(config.id));
                              setTimeout(() => setShowBuilderLogs(config.id), 1000);
                            }
                          }}
                          disabled={statuses[config.id]?.status === 'in_progress' || buildingIds.has(config.id)}
                          title="Run builder to install Composer dependencies and build Vue.js apps"
                        >
                          {buildingIds.has(config.id) ? 'Building...' : 'Run Builder'}
                        </button>
                        <button 
                          className="btn btn-sm btn-info" 
                          onClick={() => setShowBuilderLogs(config.id)}
                          title="View builder logs"
                        >
                          Builder Logs
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary" 
                          onClick={async () => {
                            setShowSyncAppEnv(config.id);
                            try {
                              const res = await axios.get(`${API_BASE}/configurations/${config.id}/app-env`);
                              setSyncAppEnvContent(res.data.appEnv || '');
                              setSyncAppEnvServerIp(res.data.serverIp || null);
                            } catch (err) {
                              console.error('Failed to load app env:', err);
                              setSyncAppEnvContent('');
                              setSyncAppEnvServerIp(null);
                            }
                          }}
                          disabled={statuses[config.id]?.status === 'in_progress'}
                          title="Edit or sync app .env to server (with EXOLOG_SERVER_IP)"
                        >
                          Sync app .env
                        </button>
                      </>
                    )}
                    <button 
                      className="btn btn-sm btn-info" 
                      onClick={() => handleShowBackups(config.id)}
                      disabled={statuses[config.id]?.status === 'in_progress'}
                      title="View backups for this configuration"
                    >
                      Restore
                    </button>
                    <button 
                      className="btn btn-sm btn-warning" 
                      onClick={() => onDelete(config.id, false)}
                      disabled={statuses[config.id]?.status === 'in_progress'}
                      title="Delete configuration only (keeps server and resources)"
                    >
                      Delete Config
                    </button>
                    <button 
                      className="btn btn-sm btn-danger" 
                      onClick={() => {
                        if (onDestroyResources) {
                          onDestroyResources(config.id);
                          setDestroyingIds(prev => new Set(prev).add(config.id));
                        }
                      }}
                      disabled={statuses[config.id]?.status === 'in_progress'}
                      title="Destroy resources (server and DNS) but keep configuration"
                    >
                      Destroy Resources
                    </button>
                    <button 
                      className="btn btn-sm btn-danger" 
                      onClick={() => onDelete(config.id, true)}
                      disabled={statuses[config.id]?.status === 'in_progress'}
                      title="Delete configuration AND destroy all resources (server, DNS, etc.)"
                    >
                      Delete & Destroy
                    </button>
                  </>
                ) : (
                  <button 
                    className="btn btn-sm btn-danger" 
                    onClick={() => onDelete(config.id, false)}
                    disabled={statuses[config.id]?.status === 'in_progress'}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {showLogs && (
        <div className="modal-overlay" onClick={() => setShowLogs(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Deployment Logs</h3>
              <button className="modal-close" onClick={() => setShowLogs(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="logs-container">
                {logs.length === 0 ? (
                  <p style={{ color: '#9ca3af' }}>No logs available yet...</p>
                ) : (
                  [...logs].reverse().map((log, index) => (
                    <div key={index} className={`log-line log-${log.type}`}>
                      <span className="log-time">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      <span className="log-message">{log.message}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showSyncAppEnv && (
        <div className="modal-overlay" onClick={() => setShowSyncAppEnv(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%', maxHeight: '90%', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3>Sync app .env</h3>
              <button className="modal-close" onClick={() => setShowSyncAppEnv(null)}>×</button>
            </div>
            <div className="modal-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {syncAppEnvServerIp && (
                <p style={{ marginBottom: '10px', color: '#6b7280' }}>
                  Server IP: <strong>{syncAppEnvServerIp}</strong> (EXOLOG_SERVER_IP will be set automatically when pushing)
                </p>
              )}
              <textarea
                value={syncAppEnvContent}
                onChange={(e) => setSyncAppEnvContent(e.target.value)}
                rows={20}
                style={{ flex: 1, minHeight: '300px', fontFamily: 'monospace', fontSize: '12px' }}
                placeholder="App .env content..."
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API_BASE}/configurations/${showSyncAppEnv}/app-env`);
                      setSyncAppEnvContent(res.data.appEnv || '');
                      setSyncAppEnvServerIp(res.data.serverIp || null);
                    } catch (err) {
                      alert('Failed to regenerate: ' + (err.response?.data?.error || err.message));
                    }
                  }}
                  disabled={syncingAppEnv}
                >
                  Regenerate
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={async () => {
                    setSyncingAppEnv(true);
                    try {
                      await axios.post(`${API_BASE}/configurations/${showSyncAppEnv}/sync-app-env`, { appEnv: syncAppEnvContent });
                      alert('App .env synced and services restarted successfully');
                      setShowSyncAppEnv(null);
                    } catch (err) {
                      alert('Failed to push: ' + (err.response?.data?.error || err.response?.data?.details || err.message));
                    } finally {
                      setSyncingAppEnv(false);
                    }
                  }}
                  disabled={syncingAppEnv}
                >
                  {syncingAppEnv ? 'Pushing...' : 'Push to server'}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => setShowSyncAppEnv(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBuilderLogs && (
        <div className="modal-overlay" onClick={() => setShowBuilderLogs(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%', maxHeight: '90%' }}>
            <div className="modal-header">
              <h3>Builder Logs {builderStatus && `(${builderStatus.status})`}</h3>
              <button className="modal-close" onClick={() => setShowBuilderLogs(null)}>×</button>
            </div>
            <div className="modal-body">
              {builderStatus && (
                <div style={{ marginBottom: '10px', padding: '10px', background: '#f3f4f6', borderRadius: '4px' }}>
                  <strong>Status:</strong> <span className={`badge badge-${builderStatus.status === 'completed' ? 'success' : builderStatus.status === 'failed' ? 'danger' : 'info'}`}>
                    {builderStatus.status}
                  </span>
                  {builderStatus.serverIp && <span style={{ marginLeft: '10px' }}><strong>Server IP:</strong> {builderStatus.serverIp}</span>}
                  {builderStatus.startedAt && <span style={{ marginLeft: '10px' }}><strong>Started:</strong> {new Date(builderStatus.startedAt).toLocaleString()}</span>}
                  {builderStatus.completedAt && <span style={{ marginLeft: '10px' }}><strong>Completed:</strong> {new Date(builderStatus.completedAt).toLocaleString()}</span>}
                  {builderStatus.error && <div style={{ marginTop: '5px', color: '#dc2626' }}><strong>Error:</strong> {builderStatus.error}</div>}
                </div>
              )}
              <div className="logs-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {builderLogs.length === 0 ? (
                  <p style={{ color: '#9ca3af' }}>No logs available yet...</p>
                ) : (
                  builderLogs.map((log, index) => (
                    <div key={index} className={`log-line log-${log.type || 'info'}`}>
                      <span className="log-time">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      <span className="log-message" style={{ whiteSpace: 'pre-wrap' }}>{log.message}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showUpdateLogs && (
        <div className="modal-overlay" onClick={() => setShowUpdateLogs(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%', maxHeight: '90%' }}>
            <div className="modal-header">
              <h3>Update Logs {updateStatus && `(${updateStatus.status})`}</h3>
              <button className="modal-close" onClick={() => setShowUpdateLogs(null)}>×</button>
            </div>
            <div className="modal-body">
              {updateStatus && (
                <div style={{ marginBottom: '10px', padding: '10px', background: '#f3f4f6', borderRadius: '4px' }}>
                  <strong>Status:</strong> <span className={`badge badge-${updateStatus.status === 'completed' ? 'success' : updateStatus.status === 'failed' ? 'danger' : 'info'}`}>
                    {updateStatus.status}
                  </span>
                  {updateStatus.serverIp && <span style={{ marginLeft: '10px' }}><strong>Server IP:</strong> {updateStatus.serverIp}</span>}
                  {updateStatus.startedAt && <span style={{ marginLeft: '10px' }}><strong>Started:</strong> {new Date(updateStatus.startedAt).toLocaleString()}</span>}
                  {updateStatus.completedAt && <span style={{ marginLeft: '10px' }}><strong>Completed:</strong> {new Date(updateStatus.completedAt).toLocaleString()}</span>}
                  {updateStatus.error && <div style={{ marginTop: '5px', color: '#dc2626' }}><strong>Error:</strong> {updateStatus.error}</div>}
                </div>
              )}
              <div className="logs-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {updateLogs.length === 0 ? (
                  <p style={{ color: '#9ca3af' }}>No logs available yet...</p>
                ) : (
                  updateLogs.map((log, index) => (
                    <div key={index} className={`log-line log-${log.type || 'stdout'}`}>
                      <span className="log-time">{log.ts ? new Date(log.ts).toLocaleTimeString() : ''}</span>
                      <span className="log-message" style={{ whiteSpace: 'pre-wrap' }}>{log.line}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showBackups !== null && (
        <div className="modal-overlay" onClick={() => setShowBackups(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{showBackups ? 'Restore Configuration' : 'All Backups'}</h3>
              <button className="modal-close" onClick={() => setShowBackups(null)}>×</button>
            </div>
            <div className="modal-body">
              {backups.length === 0 ? (
                <p style={{ color: '#9ca3af' }}>No backups available</p>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {backups.map((backup, index) => (
                    <div key={index} style={{ 
                      padding: '15px', 
                      marginBottom: '10px', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                          {new Date(backup.date).toLocaleString()}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.9em' }}>
                          {backup.configs} configuration(s)
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleRestore(backup.timestamp, showBackups)}
                        disabled={restoring}
                      >
                        {restoring ? 'Restoring...' : 'Restore'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRestoreTerraform && (
        <div className="modal-overlay" onClick={() => setShowRestoreTerraform(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Restore from Terraform Directory</h3>
              <button className="modal-close" onClick={() => setShowRestoreTerraform(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '15px', color: '#6b7280' }}>
                Enter the terraform directory ID to restore configuration from existing terraform files.
              </p>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Terraform Directory ID:
                </label>
                <input
                  type="text"
                  value={terraformId}
                  onChange={(e) => setTerraformId(e.target.value)}
                  placeholder="e.g., 8a39f7bb-dc30-442a-a753-a1d63e02e990"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  disabled={restoringTerraform}
                />
              </div>
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '4px',
                fontSize: '0.9em',
                color: '#6b7280',
                marginBottom: '15px'
              }}>
                <strong>Note:</strong> This will read terraform files (main.tf, docker-compose.yml, etc.) 
                and recreate the configuration JSON. The terraform directory must exist in the terraform/ folder.
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowRestoreTerraform(false)}
                  disabled={restoringTerraform}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleRestoreTerraformSubmit}
                  disabled={restoringTerraform || !terraformId.trim()}
                >
                  {restoringTerraform ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ConfigurationList;
